import getOverrideDisabledMessage, {
  PERIOD_STATUS_LABELS,
} from 'components/DriverAX/utils/getOverrideDisabledMessage';
import { isAdditionalPaymentPeriodLocked } from 'components/DriverAX/components/AdditionalPayments/utils';

export type AdjustOptionKey =
  | 'override'
  | 'add-recognition-payment'
  | 'manage-recognition-payments';

export type AdjustOption = {
  key: AdjustOptionKey;
  label: string;
  isDisabled: boolean;
  disabledMessage?: string;
};

export type AdjustMenuState = {
  options: AdjustOption[];
  isDisabled: boolean;
  disabledMessage?: string;
};

/**
 * What the slideout's "Adjust" menu offers for the selected record, and why
 * an option (or the whole menu) is disabled. Undefined when there is nothing
 * to offer, so the button is hidden.
 *
 * - Override follows canOverrideBonus ("pending sign off", or an elapsed week
 *   on sub-period programmes).
 * - Recognition payments can be changed in any status but closed / archived.
 *   "Manage" only shows once the record has a payment.
 */
const getAdjustOptions = ({
  selectedBonusPeriod,
  selectedSubPeriod,
  canOverride,
  canAddRecognitionPayments,
  hasRecognitionPayments,
}: {
  selectedBonusPeriod?: BonusTypes.BonusPeriod;
  selectedSubPeriod?: Period;
  // The board offers overrides and the user may make them.
  canOverride: boolean;
  // The program enables recognition payments and the user may add them.
  canAddRecognitionPayments: boolean;
  hasRecognitionPayments: boolean;
}): AdjustMenuState | undefined => {
  if (!selectedBonusPeriod) {
    return undefined;
  }

  const options: AdjustOption[] = [];
  const statusLabel = PERIOD_STATUS_LABELS[selectedBonusPeriod.status];

  if (canOverride) {
    const disabledMessage = getOverrideDisabledMessage({
      selectedBonusPeriod,
      selectedSubPeriod,
    });
    options.push({
      key: 'override',
      label: 'Override',
      isDisabled: !!disabledMessage,
      disabledMessage,
    });
  }

  if (canAddRecognitionPayments) {
    const isLocked = isAdditionalPaymentPeriodLocked(
      selectedBonusPeriod.status,
    );
    const disabledMessage = isLocked
      ? `Additional payments can't be changed once the period is "${statusLabel}".`
      : undefined;
    options.push({
      key: 'add-recognition-payment',
      label: 'Add Additional Payment',
      isDisabled: isLocked,
      disabledMessage,
    });
    if (hasRecognitionPayments) {
      options.push({
        key: 'manage-recognition-payments',
        label: 'Manage Additional Payments',
        isDisabled: isLocked,
        disabledMessage,
      });
    }
  }

  if (options.length === 0) {
    return undefined;
  }

  const isDisabled = options.every((o) => o.isDisabled);
  if (!isDisabled) {
    return { options, isDisabled };
  }

  return {
    options,
    isDisabled,
    disabledMessage: isAdditionalPaymentPeriodLocked(selectedBonusPeriod.status)
      ? `No adjustments can be made once the period is "${statusLabel}".`
      : options[0].disabledMessage,
  };
};

export default getAdjustOptions;
